import React, { useState } from 'react';

import { SportIcon, sportIconKey } from '../../components/SportIcon/SportIcon';
import type { Athlete } from './types';

const DEFAULT_PFP = '/static/default-profile-pic.png';

export function AthleteCard({ athlete }: { athlete: Athlete }) {
    const { display_name, sport, sport_icon, image, slug } = athlete;
    const iconKey = sportIconKey(sport);
    const [imageBroken, setImageBroken] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    const showFallback = !image || imageBroken;
    const href = slug ? `/@${slug}` : null;

    const content = (
        <>
            {!showFallback && !imageLoaded && (
                <div className="athlete-card__skeleton" aria-hidden="true" />
            )}
            {showFallback ? (
                <img
                    className="athlete-card__image"
                    src={DEFAULT_PFP}
                    alt={display_name}
                    loading="lazy"
                    decoding="async"
                />
            ) : (
                <picture className={`athlete-card__picture${imageLoaded ? ' athlete-card__picture--loaded' : ''}`}>
                    {image.srcsets.avif && <source srcSet={image.srcsets.avif} type="image/avif" />}
                    {image.srcsets.webp && <source srcSet={image.srcsets.webp} type="image/webp" />}
                    <img
                        className="athlete-card__image"
                        src={image.url}
                        srcSet={image.srcsets.jpeg || undefined}
                        alt={image.alt || display_name}
                        loading="lazy"
                        decoding="async"
                        onLoad={() => setImageLoaded(true)}
                        onError={() => setImageBroken(true)}
                    />
                </picture>
            )}
            <div className="athlete-card__gradient" aria-hidden="true" />
            <div className={`athlete-card__text${showFallback || imageLoaded ? ' athlete-card__text--ready' : ''}`}>
                {sport && (
                    <div className="athlete-card__sport">
                        <span className="faves-eyebrow athlete-card__sport-label">{sport}</span>
                        <span className="athlete-card__sport-icon">
                            {sport_icon
                                ? <img src={sport_icon} alt="" loading="lazy" decoding="async" />
                                : <SportIcon iconKey={iconKey} />}
                        </span>
                    </div>
                )}
                <h3 className="faves-h3 athlete-card__name">{display_name}</h3>
            </div>
        </>
    );

    if (href) {
        return (
            <a className="athlete-card" href={href} aria-label={display_name}>
                {content}
            </a>
        );
    }
    return <article className="athlete-card">{content}</article>;
}
